import { Component, OnDestroy, ViewEncapsulation } from '@angular/core';
import { FieldExpression, RangeExpression, NodeExpression, toString as luceneToString } from 'lucene';
import moment from 'moment';
import { map, Observable, Subscription } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';
import { NgFor, NgIf, DatePipe } from '@angular/common';
import { CdkScrollable } from '@angular/cdk/scrolling';

import { pageTransitions } from '../../animations/page-transitions';
import { DATETIME_FORMAT_PATTERN } from '../../models/filter-formats';
import { News } from '../../models/news-model';
import { NewsService } from '../../services/news.service';
import { TranslationService } from '../../services/translation.service';
import { OrderBy, OrderByDirection } from '../../utils/constant';
import { LocationService } from '../../services/location.service';
import { LoadingSpinnerComponent } from '../utils/loading-spinner.component';
import { VirtualScrollerDirective } from '../../directives/virtual-scroller.directive';
import { PageHeaderComponent } from '../page-header/page-header.component';

const SORT_WAY = OrderByDirection.DESC;
const SORT_BY = 'from';

@Component({
	selector: 'copo-news-list',
	templateUrl: './news-list.component.html',
	styleUrl: './news-list.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		CdkScrollable,
		PageHeaderComponent,
		VirtualScrollerDirective,
		NgFor,
		TranslateModule,

		MatButtonModule,
		RouterLink,
		MatIconModule,
		NgIf,
		LoadingSpinnerComponent,
		DatePipe
	]
})
export class NewsListComponent implements OnDestroy {
	newsFields = <const> [
		'id',
		'type',
		'from',
		'to',
		'newsContents',
		'newsContents.newsAttachments',
		'services',
		'audiences',
		'rowCreationBy'
	];

	newsList?: News[];
	newsSub?: Subscription;
	currentDate = moment(Date.now()).format(DATETIME_FORMAT_PATTERN);

	constructor(
		private readonly newsServivce: NewsService,
		readonly translationService: TranslationService,
		private readonly location: LocationService,
	) {}

	get dataId() {
		const { currentLanguage } = this.translationService;
		return `news-list-${ SORT_WAY
		}-${ SORT_BY
		}-${ String(currentLanguage) }`;
	}

	onMoreDataNeeded(index: number, limit: number, provideData: (obs: Observable<News[]>) => void) {
		provideData(
			this.newsServivce.getNews(
				this.newsFields,
				this.constructFilterNewsHistory(),
				index,
				limit,
				new OrderBy(SORT_BY, SORT_WAY),
			)
				.pipe(map((res: News[]) => {
					this.newsList = res;
					const { currentLanguage } = this.translationService;
					this.newsList = this.newsList.map(item => {
						const currentLanguageItem = item.contents
							.find(content => content.languageCode === currentLanguage
								? content : content.languageCode === 'en');
						item.title = currentLanguageItem?.title;
						item.content = this.convertNewsContentToHTML(currentLanguageItem?.content);
						return item;
					});
					return this.newsList;
				}))
		);
	}

	constructFilterNewsHistory() {
		const typeFilter: FieldExpression = {
			field: 'type',
			term: 'news',
			quoted: true
		};

		const fromDateFilter: RangeExpression = {
			field: 'from',
			term_min: '*',
			term_max: this.currentDate,
			inclusive: 'both'
		};

		const toDateFilter: RangeExpression = {
			field: 'to',
			term_min: this.currentDate,
			term_max: '*',
			inclusive: 'both'
		};

		const dateFilter: NodeExpression = {
			left: fromDateFilter,
			operator: 'AND',
			right: toDateFilter,
			parenthesized: true
		};

		const newsDateFilter: NodeExpression = {
			left: typeFilter,
			operator: 'AND',
			right: dateFilter,
			parenthesized: true
		};
		return luceneToString(newsDateFilter);
	}

	back() {
		this.location.back();
	}

	ngOnDestroy() {
		if (this.newsSub) {
			this.newsSub.unsubscribe();
		}
	}

	convertNewsContentToHTML(text: string | undefined): string | undefined {
		const urlPattern = /(https?:\/\/\S+)/g;
		// eslint-disable-next-line sonarjs/slow-regex
		const emailPattern = /([\w.-]+@[\w.-]+\.[\w-]+)/g;
		return text?.replace(urlPattern, '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>')
			.replace(emailPattern, '<a href="mailto:$1">$1</a>');
	}
}
